import { Component } from '@verndale/core';
import anime from 'animejs/lib/anime.es.js';
import { calculateLeftBorderHeight, updateHomepageDynamicStyles } from '../utils';
import { breakpoints, RUN_ANIMATION_KEY } from '../helpers/constants';


class HomepageAnimation extends Component {
  setupDefaults() {
    // Create new anime.js timeline
    this.timeline = anime.timeline({
      easing: 'easeInOutQuad',
      duration: 7000
    });
    this.wasWelcomeAnimationExecuted = window.localStorage.getItem(RUN_ANIMATION_KEY);
    this.dom = {
      html: document.querySelector('html'),
      body: document.querySelector('body'),
      scene1: this.el.querySelector('.homepage-animation__scene-1'),
      scene1Inner:this.el.querySelector('.homepage-animation__scene-1__inner'),
      scene2Background: this.el.querySelector('.homepage-animation__scene-2__background'),
      scene2Leaf: this.el.querySelector('.homepage-animation__scene-2 .homepage-animation__scene-2__leaf-container'),
      scene2Wordmark: this.el.querySelector('.homepage-animation__scene-2 .homepage-animation__scene-2__wordmark-container'),
      //Masthead elements
      homeMastheadHeading: document.querySelector('.homepage-masthead__heading'),
      homeMastheadSubHeading: document.querySelector('.homepage-masthead__sub-heading'),
      homeLeaf: document.querySelector('.homepage-masthead__header-logo'),
      homeMastheadMediaContainer: document.querySelector('.homepage-masthead__media-container'),
      homeLeftBorder: document.querySelector('.homepage-masthead__left-border'),
      // Header elements
      headerHomeLink: document.querySelector('.header__home-link'),
      headerBookNow: document.querySelector('.header__book-now__container'),
      headerControls: document.querySelector('.header__controls'),
      headerControlsButtons: [...document.querySelectorAll('.header__controls > button')],
      headerExpandedShortcuts: document.querySelector('.header__expanded-shortcuts'),
      shortcutsHeaderAnimatedElements: [...document.querySelectorAll(`
        .header__expanded-shortcuts__spacer,
        .header__expanded-shortcuts__nav li,
        .header__expanded-shortcuts .weather-button
      `)],
      // All Four elements
      homeAllFourLeaf: document.querySelector('.explore-all-four__leaf')
    };

    if (!this.wasWelcomeAnimationExecuted) {
      // Update localStorage so animation runs only on first load
      window.localStorage.setItem(RUN_ANIMATION_KEY, 'false');
      // Run specific viewport animation
      return window.innerWidth < breakpoints.tabletLandscape
        ? this.runMobileAnimation()
        : this.runDesktopAnimation();
    }
    this.hideHomepageAnimationElement();
    this.setupValuesWithoutAnimation();
  }

  hideHomepageAnimationElement() {
    this.el.classList.add('hidden');
  }

  setHtmlOverflow(value) {
    this.dom.html.style.overflowY = value;
  }

  getHomeMastheadMediaTop() {
    return this.dom.homeMastheadMediaContainer.getBoundingClientRect().top;
  }

  getAllFourLeafVerticalCenter() {
    if (!this.dom.homeAllFourLeaf) return window.innerHeight;
    const { top, height } = this.dom.homeAllFourLeaf.getBoundingClientRect();
    return top + Math.floor(height/2)
  }

  renderMastheadMediaAnimatedOverlay() {
    const mastHeadMediaAnimationBg = document.createElement('div');
    mastHeadMediaAnimationBg.classList.add('homepage-masthead__animation-background')
    this.dom.homeMastheadMediaContainer.append(mastHeadMediaAnimationBg);
    return mastHeadMediaAnimationBg;
  }

  runDesktopAnimation() {
    //Disable scroll
    this.setHtmlOverflow('hidden');
    // Get values for resizing and movement
    const homeLeafClientRect = this.dom.homeLeaf.getBoundingClientRect();
    const scene2LeafClientRect = this.dom.scene2Leaf.getBoundingClientRect();
    this.dom.scene2Leaf.classList.add('active');
    this.dom.scene2Wordmark.classList.add('active');
    this.dom.scene2Leaf.style.top = `${Math.ceil(scene2LeafClientRect.top)}px`;
    this.dom.scene2Leaf.style.left = `${Math.ceil(scene2LeafClientRect.left)}px`;
    // Render withe overlay masthead media container background
    const mastHeadMediaAnimationBg = this.renderMastheadMediaAnimatedOverlay();
    // Render animated header background
    const headerControlsAnimatedBg = document.createElement('div');
    headerControlsAnimatedBg.classList.add('header__controls__background');
    headerControlsAnimatedBg.style.height = `${this.getHomeMastheadMediaTop()}px`;
    this.dom.headerControls.append(headerControlsAnimatedBg);
    this.dom.headerControls.classList.add('with-animation');
    // Get border left height
    const leftBorderHeight = calculateLeftBorderHeight(this.dom.homeLeftBorder, false);

    /**
     * White background, logo scales down and fades
     */
    this.timeline.add({
      targets: this.dom.scene1Inner,
      opacity: [0, 1],
      scale: [1.75, 1],
      duration: 884
    }, 84);
    /**
     * Scene 1 gets removed and scene 2 gets revealed
     */
    this.timeline.add({
      targets: this.dom.scene1,
      width: ['100%', '0%'],
      duration: 718
    }, 1034);
    /**
     * Leaf change width/height and position
     */
    this.timeline.add({
      targets: this.dom.scene2Leaf,
      left: Math.floor(homeLeafClientRect.left),
      top: Math.ceil(homeLeafClientRect.top),
      width: [Math.ceil(scene2LeafClientRect.width), Math.ceil(homeLeafClientRect.width)],
      height: [Math.ceil(scene2LeafClientRect.height), Math.ceil(homeLeafClientRect.height)],
      duration: 1450
    }, 1834);
    /**
     * Black background slides right and home page gets revealed
     */
    this.timeline.add({
      targets: this.dom.scene2Background,
      translateX: ['0%', '100%'],
      duration: 688,
      complete: () => {
        this.setHtmlOverflow('auto');
        this.hideHomepageAnimationElement();
      }
    }, 3316);
    //Remove the white leaf from the screen
    this.timeline.add({
      targets: this.dom.scene2Leaf,
      opacity: [1, 0],
      duration: 100,
      easing: 'easeOutExpo'
    }, '-=260');
    /**
     * Slide down header home link
     */
    this.timeline.add({
      targets: this.dom.headerHomeLink,
      opacity: ['0', '100%'],
      translateY: ['-10%', '0%'],
      duration: 250
    }, 3616);
    /**
     * Masthead media (image/video) gets revealed
     */
    this.timeline.add({
      targets: mastHeadMediaAnimationBg,
      translateX: ['0', '-100%'],
      duration: 468,
      complete: () => mastHeadMediaAnimationBg.remove()
    }, 3600);
    /**
     * Left border slides down
     */
    this.timeline.add({
      targets: this.dom.homeLeftBorder,
      opacity: {
        value: [0, 1],
        duration: 200
      },
      height: [0, leftBorderHeight],
      duration: 716
    }, 3816);
    /**
     * Slide up header animated background
     */
    this.timeline.add({
      targets: headerControlsAnimatedBg,
      translateY: ['100%', '0%'],
      opacity: ['0', '100%'],
      duration: 368
    }, 4134);
    /**
     * Fade in header elements
     */
    this.timeline.add({
      targets: this.dom.headerControlsButtons,
      translateX: ['20%', '0%'],
      opacity: ['10%', '100%'],
      duration: 300,
      delay: anime.stagger(50)
    }, 4400);
    // Fade in shortcuts header elements
    this.timeline.add({
      targets: this.dom.shortcutsHeaderAnimatedElements,
      translateX: ['20%', '0%'],
      opacity: ['0', '100%'],
      duration: 720,
      delay: anime.stagger(100)
    }, 4466);
  }

  runMobileAnimation() {
    //Disable scroll
    this.setHtmlOverflow('hidden');
    // Render withe overlay masthead media container background
    const mastHeadMediaAnimationBg = this.renderMastheadMediaAnimatedOverlay();
    const svgHeight = this.dom.homeLeaf.offsetHeight;
    const borderTopPosition =  this.dom.homeMastheadMediaContainer.offsetTop - svgHeight/2;
    // Update left border top value
    this.dom.homeLeftBorder.style.top = `${borderTopPosition}px`;

    /**
     * Animation layer scales down and reveal header and sticky bar
     */
    this.timeline.add({
      targets: this.dom.scene1,
      scaleY: ['100%', `${(window.innerHeight - 120) * 100 / window.innerHeight}%`],
      duration: 366,
      complete: () => {
        this.setHtmlOverflow('auto');
        this.hideHomepageAnimationElement();
      }
    }, 0);
    /**
     * Fade in header control buttons
     */
    this.timeline.add({
      targets: this.dom.headerControlsButtons,
      scale: [1.2, 1],
      opacity: ['0%', '100%'],
      translateY: ['16%', '0%'],
      delay: anime.stagger(166, { direction: 'reverse' }),
      duration: 500
    }, 132);
    /**
     * Fade in and scale down header logo
     */
    this.timeline.add({
      targets: this.dom.headerHomeLink,
      scale: [1.2, 1],
      opacity: ['0%', '100%'],
      duration: 266
    }, 166);
    /**
     * Fade in book now button
     */
    this.timeline.add({
      targets: this.dom.headerBookNow,
      translateY: ['25%', '0%'],
      opacity: ['0', '100%'],
      duration: 366
    }, 366);
    /**
     * Fade in masthead heading
     */
    this.timeline.add({
      targets: this.dom.homeMastheadHeading,
      translateY: ['10%', '0%'],
      opacity: ['30%', '100%'],
      duration: 360
    }, 366);
    /**
     * Slide up masthead media container animated background
     */
    this.timeline.add({
      targets: mastHeadMediaAnimationBg,
      height:['100%', '0%'],
      duration: 300
    }, 400);
    /**
     * Scale down and fadein masthead leaf
     */
    this.timeline.add({
      targets: this.dom.homeLeaf.querySelector('svg'),
      opacity: {
        value: ['0%', '100%'],
        duration: 330
      },
      scale: [1.2, 1],
      duration: 400
    }, 400);
    /**
     * Fade in masthead sub-heading
     */
    this.timeline.add({
      targets: this.dom.homeMastheadSubHeading,
      translateY: ['10%', '0%'],
      opacity: ['0', '100%'],
      duration: 300
    }, 433);
    /**
     * Fill hone left border
     */
    this.timeline.add({
      targets: this.dom.homeLeftBorder,
      scaleY: ['0%', '100%'],
      duration: 400
    }, 566);
  }

  setupValuesWithoutAnimation() {
    updateHomepageDynamicStyles();
  }
}

export default HomepageAnimation;
